// ─── sessionExpiry.js ─────────────────────────────────────────────────────────
// Global axios response interceptor that catches expired-session responses
// (HTTP 401) from the API, clears local auth state, stores a one-shot flash
// message in sessionStorage, and redirects the user to the login page.
//
// Why this exists:
//   After the JWT expires, every API call returns 401 and individual screens
//   render raw "Unable to load …" errors with Retry buttons. That looks like
//   the app is broken. This module centralises the recovery flow so the
//   user lands on /login with a friendly "Your session has expired, please
//   log in again." banner instead.
//
// Design notes:
//   - Idempotent install via `__sessionExpiryInstalled` flag — calling
//     installSessionExpiryInterceptor() more than once is a no-op.
//   - Auth endpoints (login, refresh, 2FA verify, password reset) are
//     EXEMPT from the redirect so invalid-credential responses stay inline
//     on the login form rather than triggering a loop.
//   - We guard against multiple concurrent redirects with a module-level
//     `redirecting` flag — a single expired token typically fires several
//     parallel API calls and we want exactly one redirect.
//   - The flash uses sessionStorage (not localStorage) so it auto-clears
//     when the tab closes and does NOT survive a real fresh login.
// ──────────────────────────────────────────────────────────────────────────────

import axios from 'axios'

export const SESSION_EXPIRED_FLASH_KEY = 'session_expired_flash'

// Endpoints that legitimately return 401 during normal auth flow — do NOT
// redirect on these, otherwise a wrong-password attempt would bounce the
// user away from the login form.
const AUTH_EXEMPT_PATTERNS = [
    /\/api\/login\/?$/,
    /\/api\/2fa\/verify\/?$/,
    /\/api\/2fa\/email\/resend\/?$/,
    /\/api\/token\/refresh\/?$/,
    /\/api\/password-reset/,
    /\/api\/register/,
]

// Paths where we should NOT trigger a redirect (already on auth flow).
const AUTH_PATHS = ['/login', '/reset-password', '/setup-2fa']

let redirecting = false

function isAuthExemptUrl(url) {
    if (!url) return false
    return AUTH_EXEMPT_PATTERNS.some((re) => re.test(url))
}

function isOnAuthPage() {
    const path = window.location.pathname || ''
    return AUTH_PATHS.some((p) => path.startsWith(p))
}

function clearAuthStorage() {
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const REFRESH_KEY = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refresh_token'
    try {
        localStorage.removeItem(JWT_KEY)
        localStorage.removeItem(REFRESH_KEY)
        localStorage.removeItem('auth_user')
    } catch (_e) {
        /* localStorage can throw in some private-mode browsers — ignore */
    }
}

/**
 * Handle a confirmed expired/invalid-session response by clearing auth
 * state, storing the flash message, and redirecting to the login page.
 * Safe to call multiple times — only the first call performs the redirect.
 */
export function handleSessionExpired(message) {
    if (redirecting) return
    redirecting = true

    clearAuthStorage()

    try {
        sessionStorage.setItem(
            SESSION_EXPIRED_FLASH_KEY,
            message || 'Your session has expired. Please log in again.'
        )
    } catch (_e) {
        /* ignore storage failures */
    }

    // Use a full navigation rather than React Router so all in-flight axios
    // calls, intervals, and stores are torn down cleanly.
    if (!isOnAuthPage()) {
        window.location.replace('/login')
    }
}

/**
 * Install the global axios response interceptor. Idempotent — extra calls
 * are no-ops. Call this once at app boot, BEFORE the router renders.
 */
export function installSessionExpiryInterceptor() {
    if (typeof window === 'undefined') return
    if (window.__sessionExpiryInstalled) return
    window.__sessionExpiryInstalled = true

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status
            const url = error?.config?.url || ''

            // Only act on 401 from non-auth-flow endpoints, and only when
            // the user appears to be in an authenticated context.
            if (status === 401 && !isAuthExemptUrl(url) && !isOnAuthPage()) {
                handleSessionExpired()
                // Swallow the rejection for non-auth endpoints — the page is
                // being torn down anyway and we don't want every screen's
                // error toast to flash before the redirect.
                return new Promise(() => {})
            }

            return Promise.reject(error)
        }
    )
}

/**
 * Read and clear the one-shot flash message. Returns null if none set.
 */
export function consumeSessionExpiredFlash() {
    try {
        const msg = sessionStorage.getItem(SESSION_EXPIRED_FLASH_KEY)
        if (msg) sessionStorage.removeItem(SESSION_EXPIRED_FLASH_KEY)
        return msg || null
    } catch (_e) {
        return null
    }
}
